/**
 * FMA App Router
 * Routes for Find My Agency marketplace application
 * All FMA routes use /fma/* prefix to avoid conflicts with AMS
 */
import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { FMALayout } from "../../srcFMA/shared/components/FMALayout";
import { AccessGuard } from "@/global/components/auth/AccessGuard";
import { AuthConsume } from "@/global/components/auth/AuthConsume";
import { CookieConsentBanner } from "@/global/components/CookieConsentBanner";
import { initSentry } from "@/global/lib/sentry";

// Code-split FMA pages for better performance
const FMAHome = lazy(() => import("../pages/Home"));
const FMAAgencies = lazy(() => import("../pages/Agencies"));
const FMAAgencyProfile = lazy(() => import("../pages/AgencyProfile"));
const FMAAgentProfile = lazy(() => import("../pages/AgentProfile"));
const FMALeaderboard = lazy(() => import("../pages/Leaderboard"));
const FMATrainingVault = lazy(() => import("../pages/TrainingVault"));
const FMAAccount = lazy(() => import("../pages/Account"));
const ReferralLanding = lazy(() => import("../pages/ReferralLanding"));
const LetsGoLanding = lazy(() => import("../pages/LetsGoLanding"));
const OrcaAccountPage = lazy(() => import("@/global/pages/OrcaAccountPage"));
const AboutOrcaPage = lazy(() => import("../pages/AboutOrca"));
const PricingPage = lazy(() => import("@/global/pages/PricingPage"));
const AppChoicePage = lazy(() => import("@/global/pages/AppChoicePage"));
const TermsAndConditionsPage = lazy(() => import("@/global/pages/TermsAndConditionsPage"));
const PrivacyPolicy = lazy(() => import("../../src/pages/Legal/PrivacyPolicy"));
const PaymentSuccessPage = lazy(() => import("@/global/pages/PaymentSuccessPage"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
  </div>
);

export function FMAApp() {
  // Initialize Sentry error tracking (production only)
  useEffect(() => {
    initSentry();
  }, []);

  // Theme persistence guard: Ensure dark theme class is always present
  // Prevents white screen if localStorage theme state gets corrupted or race condition occurs
  useEffect(() => {
    const ensureThemeClass = () => {
      const html = document.documentElement;
      const hasThemeClass = html.classList.contains('dark') || html.classList.contains('light');
      
      if (!hasThemeClass) {
        // No theme class found - apply default dark theme
        html.classList.add('dark');

      }
    };

    // Check immediately on mount
    ensureThemeClass();

    // Also check after a brief delay (in case theme provider hasn't initialized yet)
    const timer = setTimeout(ensureThemeClass, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <CookieConsentBanner />
      <Routes>
        {/* SSO Token Consumption Route - No Layout */}
        <Route path="auth/consume" element={<AuthConsume />} />

        {/* Payment Success Route - No Layout */}
        <Route
          path="auth/payment-success"
          element={
            <Suspense fallback={<PageLoader />}>
              <PaymentSuccessPage />
            </Suspense>
          }
        />

      {/* Public Referral Landing Page - No Layout */}
      <Route
        path="join/:referralCode"
        element={
          <Suspense fallback={<PageLoader />}>
            <ReferralLanding />
          </Suspense>
        }
      />

      {/* Let's Go Landing Page - Social Media Optimized - No Layout */}
      <Route
        path="lets-go"
        element={
          <Suspense fallback={<PageLoader />}>
            <LetsGoLanding />
          </Suspense>
        }
      />

      {/* Privacy Policy - Public Route */}
      <Route
        path="privacy"
        element={
          <Suspense fallback={<PageLoader />}>
            <PrivacyPolicy />
          </Suspense>
        }
      />

      <Route element={<FMALayout />}>
        {/* FMA Home/Dashboard */}
        <Route
          index
          element={
            <Suspense fallback={<PageLoader />}>
              <FMAHome />
            </Suspense>
          }
        />

        {/* About Orca */}
        <Route
          path="aboutorca"
          element={
            <Suspense fallback={<PageLoader />}>
              <AboutOrcaPage />
            </Suspense>
          }
        />

        {/* Pricing Page */}
        <Route
          path="pricing"
          element={
            <Suspense fallback={<PageLoader />}>
              <PricingPage />
            </Suspense>
          }
        />

        {/* Browse Agencies */}
        <Route
          path="agencies"
          element={
            <Suspense fallback={<PageLoader />}>
              <FMAAgencies />
            </Suspense>
          }
        />

        {/* Individual Agency Profile Page */}
        <Route
          path="agencies/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <FMAAgencyProfile />
            </Suspense>
          }
        />

        {/* Agent Profile Page (requires confirmed booking to view) */}
        <Route
          path="agent-profile/:agentPersonId"
          element={
            <Suspense fallback={<PageLoader />}>
              <FMAAgentProfile />
            </Suspense>
          }
        />

        {/* Training Video Leaderboard (formerly Agency Plans) */}
        <Route
          path="leaderboard"
          element={
            <Suspense fallback={<PageLoader />}>
              <FMALeaderboard />
            </Suspense>
          }
        />

        {/* Training Vault */}
        <Route
          path="training-vault"
          element={
            <Suspense fallback={<PageLoader />}>
              <FMATrainingVault />
            </Suspense>
          }
        />

        {/* Account Hub (Profile forms for agents & agencies) */}
        <Route
          path="account"
          element={
            <Suspense fallback={<PageLoader />}>
              <FMAAccount />
            </Suspense>
          }
        />

        {/* Orca Account Settings */}
        <Route
          path="orca-account"
          element={
            <Suspense fallback={<PageLoader />}>
              <OrcaAccountPage />
            </Suspense>
          }
        />

        {/* Terms and Conditions */}
        <Route
          path="terms"
          element={
            <Suspense fallback={<PageLoader />}>
              <TermsAndConditionsPage />
            </Suspense>
          }
        />

        {/* App Choice Page - Switch between AMS and FMA */}
        <Route
          path="app-choice"
          element={
            <AccessGuard app="FMA">
              <Suspense fallback={<PageLoader />}>
                <AppChoicePage />
              </Suspense>
            </AccessGuard>
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/fma" replace />} />
      </Route>
    </Routes>
    </>
  );
}
